(() => {
  const CAROUSEL_SELECTOR = "[data-cards-carousel]";
  const BETTER_IMAGES_SELECTOR = ".tnf-sustainability__section--better img";
  const BETTER_SECTION_SELECTOR = ".tnf-sustainability__section--better";
  const BETTER_MAIN_IMAGE_SELECTOR = ".tnf-sustainability__better-main-image";
  const BETTER_SWAP_ITEM_SELECTOR = "[data-better-swap-item]";

  function initSoftLoadedImages(scope = document) {
    scope.querySelectorAll(BETTER_IMAGES_SELECTOR).forEach((img) => {
      if (img.complete) {
        img.classList.add("softLoaded");
        return;
      }

      img.addEventListener(
        "load",
        () => {
          img.classList.add("softLoaded");
        },
        { once: true }
      );

      img.addEventListener(
        "error",
        () => {
          img.classList.add("softLoaded");
        },
        { once: true }
      );
    });
  }

  function cardsPerView() {
    if (window.matchMedia("(max-width: 749px)").matches) return 1;
    if (window.matchMedia("(max-width: 989px)").matches) return 2;
    return 3;
  }

  function getGapPx(element) {
    const styles = window.getComputedStyle(element);
    const gap = parseFloat(styles.columnGap || styles.gap || "0");
    return Number.isNaN(gap) ? 0 : gap;
  }

  function initCarousel(carousel) {
    if (carousel.dataset.initialized === "true") return;

    const track = carousel.querySelector("[data-cards-track]");
    const dotsHost = carousel.querySelector("[data-cards-dots]");
    if (!track || !dotsHost) return;

    const initialCards = Array.from(track.children).filter((node) =>
      node.classList.contains("tnf-sustainability__card")
    );

    initialCards.forEach((card) => {
      card.dataset.carouselOriginal = "true";
    });

    if (initialCards.length <= 1) {
      dotsHost.hidden = true;
      return;
    }

    carousel.dataset.initialized = "true";
    carousel.classList.add("is-ready");

    let activeCardIndex = 0;
    let perView = 1;
    let dragStartX = null;
    let dragCurrentX = null;
    let dragging = false;
    let activePointerId = null;
    const supportsPointer = "PointerEvent" in window;

    function getOriginalCards() {
      return Array.from(track.children).filter(
        (node) => node.dataset.carouselOriginal === "true"
      );
    }

    function removeClones() {
      Array.from(track.querySelectorAll("[data-carousel-clone='true']")).forEach(
        (clone) => clone.remove()
      );
    }

    function buildTrailingClones() {
      removeClones();
      const originals = getOriginalCards();
      const cloneCount = Math.min(perView, originals.length);
      for (let i = 0; i < cloneCount; i += 1) {
        const clone = originals[i].cloneNode(true);
        clone.dataset.carouselClone = "true";
        clone.removeAttribute("data-carousel-original");
        track.appendChild(clone);
      }
    }

    function updateDots() {
      const originals = getOriginalCards();
      const dots = Array.from(dotsHost.querySelectorAll("button"));
      dots.forEach((dot, idx) => {
        const isActive = idx === Math.min(activeCardIndex, originals.length - 1);
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });
    }

    function setTrackOffset(index) {
      const originals = getOriginalCards();
      if (!originals.length) return;
      const cardWidth = originals[0].getBoundingClientRect().width;
      const offset = (cardWidth + getGapPx(track)) * index;
      track.style.transform = `translateX(${-offset}px)`;
    }

    function goToCard(index, options = {}) {
      const { allowClone = false } = options;
      const originals = getOriginalCards();
      if (!originals.length) return;

      if (allowClone && index === originals.length) {
        activeCardIndex = index;
      } else {
        activeCardIndex = Math.max(0, Math.min(index, originals.length - 1));
      }

      setTrackOffset(activeCardIndex);
      updateDots();
    }

    function buildDots() {
      const originals = getOriginalCards();
      dotsHost.innerHTML = "";
      for (let i = 0; i < originals.length; i += 1) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "tnf-sustainability__cards-dot";
        button.setAttribute("aria-label", `Ir a tarjeta ${i + 1}`);
        button.addEventListener("click", () => goToCard(i));
        dotsHost.appendChild(button);
      }
      dotsHost.hidden = originals.length <= 1;
    }

    function beginDrag(clientX) {
      dragStartX = clientX;
      dragCurrentX = clientX;
      dragging = true;
      carousel.classList.add("is-dragging");
    }

    function moveDrag(clientX) {
      if (!dragging) return;
      dragCurrentX = clientX;
    }

    function endDrag() {
      if (!dragging || dragStartX === null || dragCurrentX === null) {
        dragging = false;
        dragStartX = null;
        dragCurrentX = null;
        carousel.classList.remove("is-dragging");
        return;
      }

      const delta = dragCurrentX - dragStartX;
      const minSwipe = 24;
      const originals = getOriginalCards();

      if (Math.abs(delta) > minSwipe) {
        if (delta < 0) {
          if (activeCardIndex >= originals.length - 1) {
            goToCard(originals.length, { allowClone: true });
          } else {
            goToCard(activeCardIndex + 1);
          }
        }
        else {
          if (activeCardIndex <= 0) {
            goToCard(originals.length - 1);
          } else {
            goToCard(activeCardIndex - 1);
          }
        }
      }

      dragging = false;
      dragStartX = null;
      dragCurrentX = null;
      carousel.classList.remove("is-dragging");
    }

    function onTouchStart(event) {
      if (!event.touches || event.touches.length !== 1) return;
      beginDrag(event.touches[0].clientX);
    }

    function onTouchMove(event) {
      if (!event.touches || event.touches.length !== 1) return;
      moveDrag(event.touches[0].clientX);
    }

    function onTouchEnd() {
      endDrag();
    }

    function onMouseDown(event) {
      if (event.button !== 0) return;
      beginDrag(event.clientX);
    }

    function onMouseMove(event) {
      moveDrag(event.clientX);
    }

    function onMouseUp() {
      endDrag();
    }

    function onPointerDown(event) {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      activePointerId = event.pointerId;
      beginDrag(event.clientX);
      if (typeof track.setPointerCapture === "function") {
        track.setPointerCapture(event.pointerId);
      }
    }

    function onPointerMove(event) {
      if (activePointerId !== null && event.pointerId !== activePointerId) return;
      moveDrag(event.clientX);
    }

    function onPointerUp(event) {
      if (activePointerId !== null && event.pointerId !== activePointerId) return;
      endDrag();
      activePointerId = null;
    }

    function recalc() {
      const originals = getOriginalCards();
      perView = Math.min(cardsPerView(), originals.length);
      carousel.style.setProperty("--cards-per-view", String(perView));
      buildTrailingClones();
      if (activeCardIndex > originals.length - 1) activeCardIndex = originals.length - 1;
      buildDots();
      goToCard(activeCardIndex);
    }

    recalc();
    track.addEventListener("transitionend", () => {
      const originals = getOriginalCards();
      if (!originals.length) return;
      if (activeCardIndex !== originals.length) return;

      track.style.transition = "none";
      activeCardIndex = 0;
      setTrackOffset(activeCardIndex);
      track.getBoundingClientRect();
      track.style.transition = "";
      updateDots();
    });

    track.addEventListener("dragstart", (event) => event.preventDefault());
    if (supportsPointer) {
      track.addEventListener("pointerdown", onPointerDown);
      track.addEventListener("pointermove", onPointerMove);
      track.addEventListener("pointerup", onPointerUp);
      track.addEventListener("pointercancel", onPointerUp);
    } else {
      track.addEventListener("touchstart", onTouchStart, { passive: true });
      track.addEventListener("touchmove", onTouchMove, { passive: true });
      track.addEventListener("touchend", onTouchEnd);
      track.addEventListener("touchcancel", onTouchEnd);
      track.addEventListener("mousedown", onMouseDown);
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    }
    window.addEventListener("resize", recalc);
  }

  function ensureActionButton(actions, ctaIndex) {
    let button = actions.querySelector(`[data-better-cta='${ctaIndex}']`);
    if (button) return button;

    button = document.createElement("a");
    button.className = "tnf-sustainability__btn tnf-sustainability__btn--better";
    button.setAttribute("data-better-cta", String(ctaIndex));
    button.setAttribute("href", "#");
    actions.appendChild(button);
    return button;
  }

  function setActionButton(button, text, link) {
    if (!button) return;

    const hasText = Boolean(text && text.trim());
    const hasLink = Boolean(link && link.trim());

    if (!hasText || !hasLink) {
      button.style.display = "none";
      button.setAttribute("aria-hidden", "true");
      return;
    }

    button.style.display = "inline-flex";
    button.setAttribute("aria-hidden", "false");
    button.textContent = text.trim();
    button.setAttribute("href", link.trim());
  }

  function setActiveItem(items, activeIndex) {
    items.forEach((item, index) => {
      const isActive = index === activeIndex;
      item.classList.toggle("is-active", isActive);
      item.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  function syncActiveMarkers(items, activeIndex) {
    items.forEach((item, index) => {
      const isActive = index === activeIndex;
      item.querySelectorAll(".tnf-sustainability__better-side-active-marker, .tnf-sustainability__better-side-active-marker-placeholder").forEach((marker) => {
        marker.style.opacity = isActive ? "1" : "0";
        marker.style.visibility = isActive ? "visible" : "hidden";
      });
    });
  }

  function updateMainImage(mainImage, item) {
    const nextSrc = item.dataset.mainSrc;
    if (!nextSrc) return;

    const currentSrc = (mainImage.currentSrc || mainImage.src || "").split("?")[0];
    const normalizedNextSrc = nextSrc.split("?")[0];
    if (normalizedNextSrc === currentSrc) return;

    mainImage.classList.remove("softLoaded");
    mainImage.classList.add("is-swapping");
    mainImage.src = nextSrc;

    if (item.dataset.mainSrcset) {
      mainImage.srcset = item.dataset.mainSrcset;
    }

    if (item.dataset.mainAlt !== undefined) {
      mainImage.alt = item.dataset.mainAlt;
    }

    const onDone = () => {
      mainImage.classList.remove("is-swapping");
      mainImage.classList.add("softLoaded");
    };

    if (mainImage.complete) {
      onDone();
      return;
    }

    mainImage.addEventListener("load", onDone, { once: true });
    mainImage.addEventListener("error", onDone, { once: true });
  }

  function updateBetterActions(actions, item, defaults) {
    if (!actions) return;

    const cta1 = ensureActionButton(actions, 1);
    const cta2 = ensureActionButton(actions, 2);

    const cta1Text = item.getAttribute("data-cta-1-text") ?? defaults.cta1Text;
    const cta1Link = item.getAttribute("data-cta-1-link") ?? defaults.cta1Link;
    const cta2Text = item.getAttribute("data-cta-2-text") ?? defaults.cta2Text;
    const cta2Link = item.getAttribute("data-cta-2-link") ?? defaults.cta2Link;

    setActionButton(cta1, cta1Text, cta1Link);
    setActionButton(cta2, cta2Text, cta2Link);

    const visibleCount = [cta1, cta2].filter((button) => button.style.display !== "none").length;
    actions.classList.toggle("is-single", visibleCount === 1);
    actions.style.display = visibleCount > 0 ? "grid" : "none";
  }

  function preloadBetterImages(items) {
    const uniqueSources = new Set();
    items.forEach((item) => {
      const src = item.getAttribute("data-main-src");
      if (src) uniqueSources.add(src);
    });

    uniqueSources.forEach((src) => {
      const preloader = new Image();
      preloader.decoding = "async";
      preloader.src = src;
    });
  }

  function initBetterShowcaseSwap(scope = document) {
    scope.querySelectorAll(BETTER_SECTION_SELECTOR).forEach((section) => {
      if (section.dataset.swapInitialized === "true") return;

      const mainImage = section.querySelector(BETTER_MAIN_IMAGE_SELECTOR);
      const items = Array.from(section.querySelectorAll(BETTER_SWAP_ITEM_SELECTOR));
      if (!mainImage || items.length === 0) return;

      const actions = section.querySelector("[data-better-actions]");
      const defaults = {
        cta1Text: actions?.querySelector("[data-better-cta='1']")?.textContent?.trim() || "",
        cta1Link: actions?.querySelector("[data-better-cta='1']")?.getAttribute("href") || "",
        cta2Text: actions?.querySelector("[data-better-cta='2']")?.textContent?.trim() || "",
        cta2Link: actions?.querySelector("[data-better-cta='2']")?.getAttribute("href") || "",
      };

      let activeIndex = Math.max(
        0,
        items.findIndex((item) => item.classList.contains("is-active"))
      );
      if (activeIndex === -1) activeIndex = 0;

      function applyState(index, options = {}) {
        const { updateImage = true } = options;
        activeIndex = Math.max(0, Math.min(index, items.length - 1));
        const activeItem = items[activeIndex];

        setActiveItem(items, activeIndex);
        syncActiveMarkers(items, activeIndex);
        if (updateImage) {
          updateMainImage(mainImage, activeItem);
        }
        updateBetterActions(actions, activeItem, defaults);
      }

      section.dataset.swapInitialized = "true";
      applyState(activeIndex, { updateImage: false });
      preloadBetterImages(items);

      items.forEach((item, index) => {
        item.addEventListener("click", () => applyState(index));
        item.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          applyState(index);
        });
      });

      mainImage.addEventListener("click", () => {
        if (items.length < 2) return;
        applyState((activeIndex + 1) % items.length);
      });
    });
  }

  function initAll(scope = document) {
    scope.querySelectorAll(CAROUSEL_SELECTOR).forEach(initCarousel);
    initSoftLoadedImages(scope);
    initBetterShowcaseSwap(scope);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
